/**
 * Animação "produto voando pro carrinho": clona uma bolinha com a imagem do
 * produto na posição do botão clicado e a anima em arco até o ícone do
 * carrinho visível (CartButton marca-se com [data-cart-icon]). Puro DOM +
 * Web Animations API — sem estado React; o elemento se remove ao terminar.
 */
export function flyToCart(source: HTMLElement, imageUrl?: string | null): void {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // [data-cart-icon] = marcação legada; [data-slot="cart"] = botão de
    // carrinho do Header do big2be-ui.
    const target = Array.from(
        document.querySelectorAll<HTMLElement>('[data-cart-icon], [data-slot="cart"]')
    ).find((el) => el.offsetParent !== null);
    if (!target) return;

    const from = source.getBoundingClientRect();
    const to = target.getBoundingClientRect();

    const SIZE = 56;
    const startLeft = from.left + from.width / 2 - SIZE / 2;
    const startTop = from.top + from.height / 2 - SIZE / 2;
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);

    // Desvio perpendicular à direção do movimento → curva lateral.
    // O sinal segue o lado do carrinho, então a bolinha "entra de lado".
    const dist = Math.hypot(dx, dy) || 1;
    const side = dx >= 0 ? 1 : -1;
    const bend = Math.min(dist * 0.35, 140) * side;
    const perpX = (-dy / dist) * bend;
    const perpY = (dx / dist) * bend;

    const ball = document.createElement("div");
    ball.style.cssText =
        `position:fixed;left:${startLeft}px;top:${startTop}px;` +
        `width:${SIZE}px;height:${SIZE}px;border-radius:9999px;` +
        `z-index:60;pointer-events:none;overflow:hidden;` +
        `background:var(--color-base-200) center/cover no-repeat;` +
        (imageUrl ? `background-image:url("${imageUrl}");` : "") +
        `box-shadow:0 4px 16px color-mix(in srgb, black 25%, transparent);`;
    document.body.appendChild(ball);

    const animation = ball.animate(
        [
            // Pop inicial: cresce levemente antes de partir.
            { transform: "translate(0, 0) scale(0.6) rotate(0deg)", opacity: 0.9 },
            {
                transform: "translate(0, 0) scale(1.08) rotate(0deg)",
                opacity: 1,
                offset: 0.12,
            },
            // Curva lateral: pontos de controle deslocados na perpendicular.
            {
                transform:
                    `translate(${dx * 0.35 + perpX}px, ${dy * 0.35 + perpY - 40}px) ` +
                    `scale(0.9) rotate(${8 * side}deg)`,
                opacity: 1,
                offset: 0.45,
            },
            {
                transform:
                    `translate(${dx * 0.75 + perpX * 0.5}px, ${dy * 0.75 + perpY * 0.5}px) ` +
                    `scale(0.5) rotate(${14 * side}deg)`,
                opacity: 0.95,
                offset: 0.75,
            },
            // Chegada: "sugada" pelo carrinho, encolhendo até sumir.
            {
                transform: `translate(${dx}px, ${dy}px) scale(0.1) rotate(${20 * side}deg)`,
                opacity: 0,
            },
        ],
        { duration: 800, easing: "cubic-bezier(0.22, 0.8, 0.3, 1)" }
    );

    animation.onfinish = () => ball.remove();
    animation.oncancel = () => ball.remove();
}
